import React from "react";
import { Instagram, Facebook, Youtube, Smartphone } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { scrollToSection } from "@/lib/scroll";
import { apiPost } from "@/lib/api";
import { toast } from "sonner";
import { EVENT, SHOW, SOCIALS, APP } from "@/lib/data";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.78.12v-3.16a5.76 5.76 0 1 0 4.9 5.68V9.29a7.35 7.35 0 0 0 4.3 1.38V7.58a4.34 4.34 0 0 1-3.24-1.76z" />
  </svg>
);

const SOCIAL_ICONS = { instagram: Instagram, youtube: Youtube, facebook: Facebook, tiktok: TikTokIcon };

const Footer = () => {
  const [email, setEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await apiPost("/api/newsletter", { email });
      toast.success("You're on the list — first to hear about the live taping.");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please enter a valid email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="relative border-t border-[#D4AF37]/15 bg-[#090D1D] pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-3">
              <LogoMark size={44} />
              <div>
                <p className="font-cinzel text-gold-gradient tracking-[0.14em]">A VIEW FROM THE TOP</p>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A99B5] mt-1">Live with Susan Grau</p>
              </div>
            </div>
            <p className="text-sm text-[#9CA3AF] font-light mt-6 leading-relaxed">
              {EVENT.dateLabel} · Doors {EVENT.doors} · Show {EVENT.showStart}
              <br />
              {EVENT.venue} — {EVENT.address}
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICONS[s.id];
                return (
                  <a
                    key={s.id}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Susan Grau on ${s.label}`}
                    title={`${s.label} — ${s.handle}`}
                    className="w-10 h-10 rounded-full border border-[#D4AF37]/25 flex items-center justify-center text-[#F3D068] hover:bg-[#D4AF37]/10 hover:border-[#F3D068] transition-all"
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
              <a
                href={APP.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open the ${APP.name} app`}
                title={`${APP.name} app — ${APP.url}`}
                className="w-10 h-10 rounded-full border border-[#D4AF37]/25 flex items-center justify-center text-[#F3D068] hover:bg-[#D4AF37]/10 transition-all"
              >
                <Smartphone size={16} />
              </a>
            </div>
            <p className="text-[11px] text-[#8A99B5] mt-4 font-light">
              @susangrauofficial · myinfinite.app · susangrau.com
            </p>
          </div>

          <div>
            <p className="eyebrow">Explore</p>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              {[
                ["Her Story", "story"],
                ["The Live Taping", "show"],
                ["The Evening", "experience"],
                ["Meet Susan", "susan"],
                ["Seats & Tickets", "seating"],
                ["Light a Candle", "remembrance"],
                ["Gift a Ticket", "gifting"],
                ["The Book", "book"],
                ["Venue & Parking", "venue"],
                ["FAQ", "faq"],
              ].map(([label, id]) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className="text-left text-[#C5CEE0] hover:text-[#F3D068] transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="eyebrow">Tour Dates & Offerings</p>
            <p className="text-sm text-[#9CA3AF] font-light mt-5 leading-relaxed">
              First to hear about Susan's 2027 tour, her new book When Shadows Speak, and future
              live tapings of A View from the Top.
            </p>
            <form onSubmit={subscribe} className="mt-5 flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email-input"
                className="flex-1 bg-[#131B36] border border-white/10 rounded-full px-5 py-3 text-sm text-[#F8F9FA] placeholder:text-[#8A99B5]/60 focus:outline-none focus:border-[#D4AF37]/60 transition-colors"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="newsletter-submit-button"
                className="btn-gold rounded-full px-6 py-3 text-sm font-bold disabled:opacity-60"
              >
                {busy ? "…" : "Join"}
              </button>
            </form>
          </div>
        </div>

        <div className="hairline mt-14" />
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 text-xs text-[#8A99B5]">
          <p>© 2026 Susan Grau. All rights reserved.</p>
          <p className="font-light italic">
            "The people we lose are never as far as they seem." — see you under the candlelight.
          </p>
        </div>
        <div className="mt-6 text-center text-[11px] text-[#8A99B5]/70" data-testid="site-credit">
          Site created by{" "}
          <a
            href="https://mmediacorp.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="site-credit-link"
            className="text-[#F3D068]/80 hover:text-[#F3D068] underline decoration-[#D4AF37]/40 underline-offset-2 transition-colors"
          >
            M Media Corp
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
